import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { isDarkColor, relativeLuminance } from '@/lib/color-contrast';
import { TEXT_COLOR_PALETTE, findTextColor } from '../text-colors';
import { useDirectSaleTextStyle } from './useDirectSaleTextStyle';

/**
 * Above this a swatch would vanish on the paper without an outline: white,
 * cream, neon yellow.
 */
const OUTLINED_LUMINANCE = 0.75;

/**
 * The translated name of the colour the selected text wears; null while no
 * text is selected or its fill is outside the palette. The drawer shows it
 * under the "Farbe" title, where Spreadshirt names the picked colour.
 */
export function useSelectedTextColorName(): string | null {
  const { t } = useTranslation();
  const { isBound, color } = useDirectSaleTextStyle();
  const picked = isBound ? findTextColor(color) : null;
  return picked ? t(`directSale.textColors.${picked.key}`) : null;
}

/**
 * The drawer's content for "Farbe": the text palette as round swatches, in
 * the order of TEXT_COLOR_PALETTE (sortByHue). A swatch shows no text, so
 * its colour name is its accessible name; the one in use carries a check.
 * The heading belongs to the drawer.
 */
export function DirectSaleTextColorGrid() {
  const { t } = useTranslation();
  const { isBound, color, setColor } = useDirectSaleTextStyle();
  // Case-insensitive: a restored design may carry "#ef4444".
  const picked = findTextColor(color);

  return (
    <div className="grid grid-cols-5 gap-3">
      {TEXT_COLOR_PALETTE.map((entry) => {
        const active = entry.hex === picked?.hex;
        const name = t(`directSale.textColors.${entry.key}`);
        const luminance = relativeLuminance(entry.hex) ?? 0;
        return (
          <button
            key={entry.key}
            type="button"
            aria-label={name}
            title={name}
            aria-pressed={active}
            // Without a text there is nothing to colour.
            disabled={!isBound}
            onClick={() => setColor(entry.hex)}
            style={{ backgroundColor: entry.hex }}
            className={cn(
              'flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:hover:scale-100',
              luminance >= OUTLINED_LUMINANCE && 'border border-line'
            )}
          >
            {active && (
              <Check
                aria-hidden="true"
                className={cn(
                  'h-5 w-5',
                  // The swatch is a literal print colour, not a theme token,
                  // so the check is literally white or ink on it.
                  isDarkColor(entry.hex) ? 'text-white' : 'text-ink'
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
